import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useAuth, useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { alertCollectionPath } from '../schemas/paths';
import { updateAlert } from '../callables';
import type { TheaWebAlert, TheaWebAlertType } from '../schemas/alert';
import {
  getDismissedAlertIds,
  markAlertDismissed,
  reconcileDismissedAlertIds,
  resetDismissedAlertIds,
  subscribeDismissedAlertIds,
  unmarkAlertDismissed,
} from './alertDismissStore';

/**
 * Phase 6b alerts hook: subscribes to `theaWebUser/{uid}/alert` filtered
 * to non-dismissed docs and exposes a `dismiss(alertId)` that calls the
 * `theaWebUpdateAlert` callable.
 *
 * Replaces the Phase 6a client-computed alerts hook. The BE owns the
 * data now — `theaWebRecomputeAlerts` (daily) + the recipient trigger's
 * immediate-fire branch keep the subcollection fresh. Dismissals are
 * cross-device (a dismiss on phone shows up dismissed on laptop on the
 * next snapshot).
 *
 * Two alert types ship in Phase 6b:
 *   - `add_birthday`: recipient without a birthday set
 *   - `coming_up_soon`: any event in next 7 days
 *
 * (`bought_any` was cut 2026-06-11 — may return. See schemas/alert.ts.)
 *
 * Optimistic dismiss: clicking the × removes the alert immediately so the
 * dropdown updates without waiting for the BE round-trip. If the callable
 * fails, the alert reappears on the next snapshot. The pending-dismiss set
 * lives in a module-level store (`alertDismissStore.ts`), not local state —
 * `useAlerts()` mounts independently at multiple points in the same tab
 * (the You-tab dot, the desktop menu, the mobile You page) and a dismiss on
 * one must be visible to all of them instantly, not just to the instance
 * that handled the click (THE-995).
 */

export type AlertType = TheaWebAlertType;

export interface Alert {
  alertId: string;
  alertKey: string;
  type: AlertType;
  /** User-facing headline derived from alertType + payload. Kept short
   *  so the dropdown row fits in one or two lines. */
  headline: string;
  /** Click-target route. Recipient-scoped alerts route to that
   *  recipient's board; user-scoped (none today) would route to
   *  whatever surface the alertType implies. */
  href: string;
  recipientId?: string;
  emoji?: string;
  /** True when a coming_up_soon alert's event date is TODAY in the
   *  viewer's LOCAL calendar. Drives the day-of celebration styling
   *  in AccountAlerts. */
  isToday?: boolean;
  /** True when the event's date has already passed in the viewer's
   *  LOCAL calendar. The BE retains the alert one extra day so every
   *  timezone gets its full day-of; `useAlerts` drops isPast rows from
   *  `alerts`/`count` (and AccountAlerts hides them again, defensively)
   *  rather than showing a stale "today". */
  isPast?: boolean;
}

interface AlertPayload {
  recipientName?: string;
  recipientEmoji?: string;
  savedCount?: number;
  eventType?: string;
  nextEventAtIso?: string;
  /** Self boards — render second person ("Your birthday"). Alerts
   *  written before 2026-07-14 lack it; treat missing as false. */
  isMe?: boolean;
}

function eventLabel(eventType: string | undefined): string {
  if (eventType === 'birthday') return 'birthday';
  if (eventType === 'anniversary') return 'anniversary';
  return eventType ?? 'event';
}

/** Calendar-day diff between the event's date and the viewer's LOCAL
 *  date. Event side reads UTC fields (the BE encodes the date as
 *  midnight UTC — it's a date, not a moment); `now` side reads LOCAL
 *  fields ("today" means today where the viewer is standing). Same
 *  two-calendar rule as ComingUpStrip.daysFromToday — see its comment
 *  for the 2026-07-10 bug this prevents. Returns null when the iso is
 *  missing/unparseable. */
function calendarDaysFromToday(iso: string | undefined): number | null {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  const now = new Date();
  const eventCal = Date.UTC(
    when.getUTCFullYear(),
    when.getUTCMonth(),
    when.getUTCDate(),
  );
  const todayCal = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((eventCal - todayCal) / 86_400_000);
}

function formatRelativeDays(days: number | null): string {
  if (days === null) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 7) return `in ${days} days`;
  const weeks = Math.round(days / 7);
  return weeks === 1 ? 'in 1 week' : `in ${weeks} weeks`;
}

/** "a birthday" / "an anniversary" — article for name-less headlines. */
function withArticle(label: string): string {
  return `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label}`;
}

function alertToFE(doc: TheaWebAlert): Alert {
  const payload = (doc.payload ?? {}) as AlertPayload;
  // The BE writes recipientName as "" when the recipient has no name, so
  // an empty/whitespace value must count as missing too — `?? 'them'`
  // let "" through and produced "Add 's birthday" (and non-empty misses
  // produced "Add them's birthday"). Name-less alerts get their own
  // sentence shapes below instead of a placeholder word.
  const rawName = (payload.recipientName ?? '').trim();
  const hasName = rawName.length > 0;
  let headline = '';
  let href = '/people';
  let isToday = false;
  let isPast = false;
  // Emoji is ALWAYS type-based (🎂 birthday, 💕 anniversary), never the
  // recipient's own emoji — mixing them made the list read inconsistent
  // (Kate 2026-07-06: some rows cake, one row the recipient's ✨). The row
  // already carries the name; the glyph's job is to say what KIND of
  // alert this is.
  let emoji = '🎂';

  switch (doc.alertType) {
    case 'add_birthday':
      headline = payload.isMe
        ? 'Add your birthday'
        : hasName
          ? `Add ${rawName}'s birthday`
          : 'Add a birthday';
      // ?about=1 deep-links into the About sheet (where the birthday
      // pickers live) — landing on the bare board left users hunting for
      // where to actually add the date. BoardRoute forwards the query
      // string through its redirect.
      href = doc.recipientId ? `/board/${doc.recipientId}?about=1` : '/people';
      break;
    case 'coming_up_soon': {
      const label = eventLabel(payload.eventType);
      const days = calendarDaysFromToday(payload.nextEventAtIso);
      const when = formatRelativeDays(days);
      if (payload.isMe) {
        // Second person for self boards — "Me's birthday in 3 days" was
        // the board name leaking into the sentence (2026-07-14 audit).
        headline = when ? `Your ${label} ${when}` : `Your ${label} soon`;
      } else if (hasName) {
        headline = when
          ? `${rawName}'s ${label} ${when}`
          : `${rawName}'s ${label} soon`;
      } else {
        // No name: "A birthday in 4 days" / "An anniversary coming up soon".
        const subject = withArticle(label);
        headline = when
          ? `${subject[0].toUpperCase()}${subject.slice(1)} ${when}`
          : `${subject[0].toUpperCase()}${subject.slice(1)} coming up soon`;
      }
      isToday = days === 0;
      isPast = days !== null && days < 0;
      // Route to the recipient's own board (not the all-people list) —
      // "Ashley's birthday in 4 days" should land on Ashley.
      href = doc.recipientId ? `/board/${doc.recipientId}` : '/people';
      emoji = payload.eventType === 'anniversary' ? '💕' : '🎂';
      break;
    }
  }

  return {
    alertId: doc.alertId,
    alertKey: doc.alertKey,
    type: doc.alertType,
    headline,
    href,
    recipientId: doc.recipientId,
    emoji,
    isToday,
    isPast,
  };
}

export interface UseAlertsResult {
  alerts: Alert[];
  count: number;
  /**
   * True once the alert listener has delivered its first snapshot for the
   * current uid (and false again the moment the uid changes).
   *
   * ADDITIVE, and deliberately additive: no existing consumer reads it, so
   * every surface that renders alerts today — `V4AccountDropdown`,
   * `HeaderAccountMenu`, `BoardAlertsBell`, the legacy `YouPage` branch — is
   * behaviourally unchanged. It exists because the Hub's account surfaces
   * (THE-967 PR 1 / PR 3) must never paint "You're all caught up." before the
   * data arrives, and `alerts.length === 0` cannot tell "no alerts" from "not
   * asked yet". `useRecipients` and `useAllSavedActivities` already expose
   * exactly this flag under exactly this name; this is the third instance of
   * the pattern, not a fourth mechanism.
   *
   * A snapshot ERROR deliberately leaves this FALSE. The listener retries
   * automatically and the last good list is kept (see the error handler), so a
   * consumer that wants to offer a retry affordance after a few seconds needs
   * the un-hydrated state to persist rather than being told "loaded, empty".
   */
  hydrated: boolean;
  /** Optimistically removes from local state + fires the callable. If
   *  the callable fails, the alert reappears on the next snapshot. */
  dismiss: (alertId: string) => void;
}

export function useAlerts(): UseAlertsResult {
  const auth = useAuth();
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const optimisticallyDismissed = useSyncExternalStore(
    subscribeDismissedAlertIds,
    getDismissedAlertIds,
    getDismissedAlertIds,
  );

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      const nextUid = user?.uid ?? null;
      setUid((prev) => {
        if (prev === nextUid) return prev;
        setAlerts([]);
        setHydrated(false);
        resetDismissedAlertIds();
        return nextUid;
      });
    });
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    if (typeof ensureAuth !== 'function') return;

    // Bind the identity this effect run was SCHEDULED for, not whatever
    // `ensureAuth()` happens to resolve. During an anon→permanent transition
    // `ensureAuth()` can hand back the OLD uid a beat late (a stale in-flight
    // anon sign-in, or a separate hook instance); `uid` from the authoritative
    // `onAuthStateChanged` is this run's real identity (a newer one would have
    // cancelled it). `resolvedUid` is used ONLY to bootstrap the null case
    // (cold load / fresh anon mint), so a settled account is never stranded
    // without a subscription.
    const expectedUid = uid;

    ensureAuth()
      .then((resolvedUid) => {
        if (cancelled) return;
        // Known identity → bind it directly; `resolvedUid` bootstraps null.
        const targetUid = expectedUid ?? resolvedUid;
        const ref = collection(db, ...alertCollectionPath(targetUid));
        // Active alerts only — dismissed docs stay in the collection
        // (audit trail) but don't render. orderBy createdAt desc surfaces
        // newest first in the dropdown.
        const q = query(
          ref,
          where('dismissedAt', '==', null),
          orderBy('createdAt', 'desc'),
        );
        unsubscribe = onSnapshot(
          q,
          (snap) => {
            if (cancelled) return;
            const next: Alert[] = [];
            snap.forEach((d) => {
              const data = d.data() as TheaWebAlert;
              next.push(alertToFE({ ...data, alertId: d.id }));
            });
            // Clear optimistic dismissals once the BE has caught up
            // (the dismissed docs drop out of the snapshot). Any one
            // `useAlerts()` mount's snapshot confirming this is enough —
            // see alertDismissStore.ts.
            reconcileDismissedAlertIds(new Set(next.map((a) => a.alertId)));
            setAlerts(next);
            setHydrated(true);
          },
          () => {
            if (cancelled) return;
            // Snapshot error (rules failure, network blip). Keep the
            // last successful list rather than blanking out — the
            // listener will retry automatically.
          },
        );
      })
      .catch(() => {
        // ensureAuth rejection. Caller sees empty alerts list.
      });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [uid, db, ensureAuth]);

  const dismiss = useCallback(
    (alertId: string) => {
      // Optimistic removal — alert disappears from every `useAlerts()`
      // mount immediately, then the callable lands and the snapshot
      // confirms.
      markAlertDismissed(alertId);
      updateAlert({ alertId, dismiss: true } as never).catch(() => {
        // Callable failed (auth lapse, network). Roll back the optimistic
        // state so the user sees the alert again.
        unmarkAlertDismissed(alertId);
      });
    },
    [],
  );

  // `isPast` rows drop out HERE, not just at render time. The BE retains a
  // coming_up_soon alert up to 2 days past its event so every timezone keeps
  // its full local day-of (theaWebCommon/alerts.py::is_coming_up_soon_eligible),
  // and the FE is the layer that knows the viewer's calendar has moved on.
  // AccountAlerts already hid those rows — but `count` didn't, so the avatar /
  // You-tab badge advertised a notification that no surface could show: a red
  // "1" over a Notifications section that rendered nothing (Kate, 2026-10-03).
  // Filtering at the source keeps the badge, the list, and the "You're all
  // caught up" empty state reading from one set.
  const visible = alerts.filter(
    (a) => !optimisticallyDismissed.has(a.alertId) && !a.isPast,
  );

  return {
    alerts: visible,
    count: visible.length,
    hydrated,
    dismiss,
  };
}
